
import { Component, OnInit, forwardRef, signal, computed, input, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormsModule } from '@angular/forms';

export interface Option {
    [key: string]: any;
}

@Component({
    selector: 'rs-multiselect',
    templateUrl: './rs-multiselect.component.html',
    styleUrls: ['./rs-multiselect.component.scss'],
    imports: [FormsModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RsMultiselectComponent),
            multi: true,
        },
    ]
})
export class RsMultiselectComponent implements OnInit, ControlValueAccessor {
    options = input<Option[]>([]);
    placeholder = input('Select items');

    config = input<{
        labelKey?: string;
        valueKey?: string;
    }>({
        labelKey: 'label',
        valueKey: 'value',
    });

    selectedValues = signal<string[]>([]);
    searchTerm = signal('');

    @Output() selectionChange = new EventEmitter<string[]>();

    private onChange: (value: any) => void = () => {};
    private onTouched: () => void = () => {};

    get searchTermModel() {
        return this.searchTerm();
    }

    set searchTermModel(value: string) {
        this.searchTerm.set(value);
    }

    filteredOptions = computed(() => {
        const term = this.searchTerm().toLowerCase();
        const options = this.options() ?? [];
        const { labelKey, valueKey } = this.computedConfig();
        const selected = this.selectedValues();

        return options
            .filter((option) => {
                const label = option[labelKey];
                return typeof label === 'string' && label.toLowerCase().includes(term);
            })
            .sort((a, b) => {
                const aSelected = selected.includes(a[valueKey]);
                const bSelected = selected.includes(b[valueKey]);

                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;
                return 0;
            });
    });

    get selectedLabels(): string[] {
        const { valueKey, labelKey } = this.computedConfig();
        return (
            this.options()
                ?.filter((option) => this.selectedValues().includes(option[valueKey]))
                .map((option) => option[labelKey]) || []
        );
    }

    computedConfig = computed(() => ({
        ...this.config(),
        labelKey: this.config().labelKey ?? 'label',
        valueKey: this.config().valueKey ?? 'value',
    }));

    toggleSelection(option: Option) {
        const { valueKey } = this.computedConfig();
        const val = option[valueKey];
        const current = this.selectedValues();
        let updated: string[];

        if (current.includes(val)) {
            updated = current.filter((v) => v !== val);
        } else {
            updated = [...current, val];
        }

        this.selectedValues.set(updated);
        this.selectionChange.emit(updated);
        this.onChange(updated);
        this.onTouched();
    }

    isSelected(option: Option) {
        return this.selectedValues().includes(option[this.computedConfig().valueKey]);
    }

    writeValue(value: any): void {
        if (Array.isArray(value)) {
            this.selectedValues.set(value);
        } else {
            this.selectedValues.set([]);
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    ngOnInit(): void {}
}
